import React from 'react';
import { Grid } from '@mui/material';
import { InfoCard, SparkDataSearchBar } from 'sharedComponents/UI';
import TypeDetailsContainer from './TypeDetailsContainer';
import type { Plugins } from 'types/Plugins';
import { useInfinitePluginQuery } from 'hooks/SparkBuilder/pluginFunction/useInfinitePluginQuery';
import { usePluginSearch } from 'hooks/SparkBuilder/pluginFunction/usePluginSearch';
import { getIsSearchableQuery } from 'utilities/index';
import StepSelectorItemSkeleton from 'sharedComponents/Skeleton/StepSelectorItemSkeleton';
import PluginToggle from './PluginToggle';
import { useGetTags } from 'hooks/SparkBuilder/pluginFunction/pluginConnectionsAndParameters/useTags';

type Props = {
  onClick: (selectedStepTypeID: string, resourceID?: string) => void;
  typeID: string;
};

function PluginModal({ onClick, typeID }: Props) {
  const [query, setQuery] = React.useState('');
  const [selectedTag, setSelectedTag] = React.useState<string>('');
  const [searchedPlugins, setSearchedPlugins] = React.useState<Plugins[]>([]);
  const { plugins, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfinitePluginQuery(!query);
  const { idMap } = useGetTags();

  const { mutateAsync: searchPlugins, isLoading: isSearchingForPlugins } =
    usePluginSearch();

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage || !!query) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, query]);

  const handleChange = async (query: string) => {
    const isSearchableQuery = getIsSearchableQuery(query);
    setQuery(query);

    if (!isSearchableQuery) {
      setSearchedPlugins([]);
      return;
    }

    if (searchedPlugins.length) {
      setSearchedPlugins((prev) =>
        prev.filter((prevSpark) =>
          prevSpark.attributes.name?.toLowerCase().includes(query.toLowerCase())
        )
      );
      return;
    }
    const tagName = (idMap && idMap[selectedTag || '']?.attributes.key) || '';
    const response = await searchPlugins({
      queryString: query,
      tag: tagName,
    });
    const plugins = response.data;
    if (plugins && plugins?.length > 0) {
      setSearchedPlugins(plugins);
    }
  };

  const handleTagChange = async (tagID: string) => {
    setSearchedPlugins([]);

    setSelectedTag(tagID);
    const tagName = (idMap && idMap[tagID || '']?.attributes.key) || '';

    const response = await searchPlugins({
      queryString: query,
      tag: tagName,
    });
    const plugins = response.data;
    if (plugins && plugins?.length > 0) {
      setSearchedPlugins(plugins);
    }
  };

  const pluginsToRender = React.useMemo(() => {
    if (searchedPlugins.length) {
      return searchedPlugins;
    }

    return plugins;
  }, [plugins, searchedPlugins]);

  const renderSearchResults = React.useMemo(() => {
    const isSearchableQuery = getIsSearchableQuery(query);

    if (!!query && !isSearchableQuery) {
      return (
        <Grid container>
          <Grid item xs={12}>
            <InfoCard
              cardStyles={{ width: '100%' }}
              info='Please enter at least 3 characters'
            />
          </Grid>
        </Grid>
      );
    }

    if (isSearchableQuery && !searchedPlugins.length) {
      if (isSearchingForPlugins) {
        return (
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} key={index}>
                <StepSelectorItemSkeleton />
              </Grid>
            ))}
          </Grid>
        );
      }
      return (
        <Grid container>
          <InfoCard
            cardStyles={{ width: '100%' }}
            info={`0 results for "${query}"`}
            type='error'
          />
        </Grid>
      );
    }

    return (
      <Grid container>
        <Grid item xs={12}>
          <TypeDetailsContainer
            onLastElementView={handleLatElementView}
            plugins={pluginsToRender}
            isLoading={isLoading || isSearchingForPlugins}
            onClick={onClick}
            typeID={typeID}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Grid>
      </Grid>
    );
  }, [
    handleLatElementView,
    isFetchingNextPage,
    isLoading,
    isSearchingForPlugins,
    onClick,
    pluginsToRender,
    query,
    searchedPlugins.length,
    typeID,
  ]);

  return (
    <Grid container spacing={2} height={540}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SparkDataSearchBar
              setQuery={handleChange}
              placeholder='Search Plugins'
              sx={{ width: '100%' }}
            />
          </Grid>
          <PluginToggle
            selectedTag={selectedTag}
            setSelectedTag={handleTagChange}
          />

          <Grid item xs={12}>
            {renderSearchResults}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default PluginModal;
